import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createVideoSkill: Skill = {
    name: SKILL_NAMES.createVideo,
    description:
        'Generic video with a wide variety of scene designs, told through one continuous, informative narration. The default recipe — load it whenever no more specific recipe matches the request.',
    kind: 'video',
    surfaces: ['sketch'],
    // Offered as a tile too, under the name the format goes by: it is still the
    // fallback the agent loads when nothing more specific matches, but a user
    // who wants a plain explainer should be able to ask for one directly rather
    // than reaching it only by not matching anything else.
    dashboard: {
        title: 'Explaining Ideas',
        preview: 'explaining-ideas',
        frames: [
            'explaining-ideas-1',
            'explaining-ideas-2',
            'explaining-ideas-3',
            'explaining-ideas-4',
            'explaining-ideas-5',
        ],
    },
    instructions: `# Create Video (Lumen5 Video Recipe)
Lumen5 best practices for a general-purpose video. This is the fallback recipe:
load it whenever the request doesn't match a more specific one. The goal is a
professionally made video that teaches the viewer something, told as one
continuous, informative script running across a wide variety of scene designs.

## Setup
Before building, gather what you need with a single \`${TOOL_NAMES.askUserQuestion}\` call —
skip any item the user already gave you in their request:
- **Aspect ratio** — type \`aspect_ratio\`.
- **Duration** — radio with common presets (e.g. ~30s, ~60s, ~90s+) plus the
  automatic Other field for a specific target.
- **Closing call-to-action** — radio yes/no: should the video end on a CTA, or
  just a wrap-up/takeaway with no ask attached?
- **Voice and tone for the script** — free_text: any specific voice or tone they
  want (e.g. playful, authoritative, casual)? Default, if they have no
  preference, is a professional corporate tone.
Apply the aspect ratio with \`${TOOL_NAMES.setAspectRatio}\` before building any scenes —
designs are laid out per aspect ratio, so changing it later reworks the video.
Pick a template that matches the subject and the requested tone; search if
none of the available ones seem an obvious fit.

## Narration
- **Default is an AI voiceover in a professional corporate tone**, with
  professional corporate background music — unless the user's answers above
  said otherwise, or unless they gave you narration-source material that
  implies a different build: a talking-head video of someone speaking on
  camera (load \`${SKILL_NAMES.talkingHeadVideoBasics}\`), their own recorded voiceover audio
  (load \`${SKILL_NAMES.recordedVoiceoverVideoBasics}\`), or an explicit ask for a
  text-only/silent video (load \`${SKILL_NAMES.textVideoBasics}\`). Otherwise load
  \`${SKILL_NAMES.voiceoverVideoBasics}\` for the build mechanics. If it's unclear which they
  meant, ask.
- Pick a voice matching the requested tone (or the professional-corporate
  default) and any accent/language/audience info you have.
- **Never use em dashes (—)** in the script or in on-screen text. Write the two
  halves as separate sentences, or join with a comma or conjunction.
- Well-placed pauses give the narration room to breathe — a beat after the
  opening hook, before a key point, ahead of the closing line. Sparingly, not
  between every sentence.

## Script — one continuous, informative script
- Write the narration as **one continuous piece of prose** — a single flowing
  explanation the video walks through — not a string of disconnected,
  standalone facts. Each scene is a visual beat of that one script, not an
  isolated statement bolted on next to unrelated ones.
- Tone is **informative, communicative, educational, and instructional**: the
  video should teach the viewer something and walk them through it in a clear,
  logical order — plainspoken and direct, not hype-y ad copy and not flowery
  narration.
- **The governing rule for how much text belongs in a scene: exactly the
  words spoken during that scene's on-screen time — no more, no less.**
  Text amount isn't a separate goal to maximize or minimize; it's derived
  from the audio. If the spoken line is "our revenue grew forty percent this
  year," the on-screen text is that line (or very close to it) — not a
  shortened "forty percent," and not that line plus extra written commentary
  that was never spoken. Trimming happens in the script itself — write a
  shorter spoken sentence — never by showing less text than what's playing,
  and never by padding the screen with more than what's playing.
- **Only use a scene's secondary text asset (subtitle, body copy) when the
  spoken words for that scene naturally split across headline and subtitle**
  — for example a short title phrase plus the sentence that follows it in
  the same breath. Don't populate it just to avoid leaving it blank; a
  secondary asset with nothing genuinely spoken to put there should stay
  empty. Filling it with unspoken filler text is exactly what causes a scene
  to show more than a viewer has time to read.
- **Prefer more, shorter scenes over fewer, longer ones.** When there's more
  to say, split it into additional scene beats — each covering one complete
  sentence or a single idea — rather than stacking multiple points into one
  scene and letting its duration stretch to fit. More total scenes, each
  brief and focused on one thought, keeps pacing snappy and makes the video
  easier to follow. This is about scene count, not text volume — each
  scene's text is still governed by the rule above, sized to what's actually
  spoken in it.
- Build the narrative spine first — the ordered ideas the video must hit —
  before exact wording, so one changed scene doesn't force a full rewrite.
- **Open on a title scene** with a specific title that says exactly what the
  video is about — never a cold drop into the first fact. **Close on a real
  outro**: if the user wants a CTA, make the close a direct call to action; if
  not, close on a genuine wrap-up or takeaway line — never an abrupt stop after
  the last point.
- For each scene, decide whether media is central to the point (a specific
  product, place, thing) or illustrative background, and plan that scene's
  wording and media together — don't write first and hope matching media turns
  up.
- **List and count fidelity.** If a scene is a running list — even 3 items —
  mark it for a list-format design and capture the complete set of items. When
  narration states a count, the visuals show every item and the numbers match.
  If a design can't hold all the items, split the list across scenes or pick a
  design that can.
- If narration references step numbers ("step 1", "the second reason"), the
  design must actually display that number — spoken-only numbers are a defect.
- Write final wording budgeted to the target duration from Setup — scenes and
  word count fit the duration, not the other way around — and word it with the
  build in mind: natural clause breaks where overlay splits will land.

## Scene design variety
- **Use a wide variety of scene designs across the video.** Actively rotate
  across categories over the course of the video — including but not limited
  to title, media, collage, text, quote, lists, data/chart, animated captions,
  and icon designs — rather than leaning on two or three. Repetition is only
  fine when content genuinely calls for a matching treatment (items of one
  list), and even then, not endlessly.
- Never the same design on two consecutive scenes.
- **Match the design to the content**: does each text asset comfortably fit
  what you're putting in it (shape-bounded assets — circles, badges, pills —
  hold one or two words, no more)? Does the asset count match what the scene
  has to say? A design missing a needed asset is the wrong design; it can't be
  retrofitted.
- **A Quote design is for an actual attributed quotation** — someone's words,
  with a real name to credit them to. It is not a styling option for a strong
  line, a rhetorical question, a tagline, or a statistic. The decorative
  quotation mark is usually a read-only asset you cannot remove, so blanking
  the name/role slots leaves it stranded over copy nobody said. If you don't
  have a speaker to credit, use a text/title design instead — no matter how
  much the variety rule wants another category. Most quote designs supply the
  quotation mark themselves, so don't add quote characters unless the design's
  own placeholder text already has them.
- List-like content gets a listicle/list design whose asset count fits the item
  count. Title/intro designs only at the open; outro/disclaimer only at the
  end.
- **Avoid back-to-back text-only scenes** — after a text scene, the next one
  should carry media.
- **Caption-style designs** (ANIMATED_CAPTIONS category) are a good deliberate
  choice for a scene with no strong media — not a default, and not never.
- **Text**: on-screen text must stand on its own and match, word for word,
  what's actually being spoken during that scene — no more, no less. Favor a
  full clause or sentence over a terse fragment or single word, but only
  populate a secondary text asset when there are genuinely more spoken words
  for that scene to hold there, not to avoid leaving it blank. Keep each
  scene to one idea: if there's more to say than this scene's actual spoken
  span comfortably holds, add another scene rather than stretching this
  one's duration, shrinking the type, or padding it with unspoken text. Aim
  for a font size around 60 or above. In a block of 6+ words, highlight the
  key word or phrase.

## Asset timing — anchor multi-point speech precisely
- **When the script speaks multiple distinct points in sequence** (a list of
  reasons, steps, comparisons, benefits), don't cover the whole passage with
  one loosely-timed overlay per sentence. Anchor each point's on-screen text to
  the exact words where that point is spoken, using \`${TOOL_NAMES.addVoiceoverScene}\` with
  the \`words\` for just that point — so point two's text appears exactly when
  point two is spoken, not before or after it.
- **The on-screen text must match the words its overlay's \`words\` range
  covers — not a trimmed-down highlight of them, and not padded past them.**
  If an overlay's \`words\` span is "our revenue grew forty percent this
  year," the text asset shows that full span — not just "forty percent,"
  and not that span plus extra unspoken text stuffed into a secondary text
  asset to look fuller. A mismatch between what's heard and what's shown in
  either direction is a defect: showing less than what's said, or showing
  more.
- **Size each overlay's word-span to a natural, readable chunk — not a
  fragment, and not a run-on.** Because a word-bound overlay's on-screen
  time is set by how long those words take to say, the words you choose for
  \`words\` directly control how long the text stays up: too few words (two or
  three) and the text flashes by before it can be read even though it's
  perfectly synced; too many and the scene runs long or the text feels
  cramped. Split at natural clause or sentence boundaries — a comma, a
  conjunction, a full stop — so each overlay covers a complete clause or
  short sentence, not an arbitrary word count. As a rough feel: if a span
  would be spoken in much under two seconds, it's probably too short to
  read comfortably and belongs merged with its neighbor; if it runs past
  four or five seconds, look for a natural place to split it. This is the
  same target as \`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\` check — use it to catch
  spans on either end of that range.
- Cut a full sentence into two or more overlays/subscenes only at those
  natural break points, never mid-phrase and never just to hit a shorter
  scene count. When one sentence spans several overlays, keep its design and
  media constant across them so it reads as one continuous moment.
- **Bias toward more, shorter scenes over fewer, longer ones — but "shorter"
  means cutting at the next natural clause boundary, not shaving words off
  an overlay's span.** A narration video should read as a run of short,
  precisely-timed visual moments, each holding one complete idea — not a
  paraphrase of that idea, and not a fragment too brief to read. When a
  passage has more to say, reach for another scene or subscene at the next
  natural break, rather than either (a) letting one scene run long to fit
  everything, or (b) chopping the words shown down to a sliver that's
  technically in sync but gone before a viewer can read it. The right pace
  feels brisk but readable — never a flicker.

## Media
- The chosen clip should obviously fit the point — a viewer half-paying-
  attention should get why it's on screen. Vaguely-on-topic is not enough;
  search again rather than settle.
- Load \`${SKILL_NAMES.findUserMedia}\` before going through the user's own library,
  \`${SKILL_NAMES.findStockMedia}\` before your first stock search, and \`${SKILL_NAMES.findDamMedia}\` too
  when a DAM is connected — brand/company content likely lives there.
- Reframe within the asset so the subject is well-composed and nothing
  important sits under text (\`${SKILL_NAMES.cropMedia}\` skill).
- **Stats worth seeing become charts**, not plain text.

## Captions, music, and styling
- **Captions**: turn them off by default (\`${TOOL_NAMES.setCaptions}\` with
  \`enabled: false\`) unless the user specifically asks for captions on.
- **Music**: add professional corporate background music by default, matching
  the requested tone if one was given — unless the user says otherwise.
- Colors and styling come from the template or the user's brand kit — apply a
  kit when one exists or the user references their brand (\`${SKILL_NAMES.applyBrandKit}\`
  skill); don't hand-pick color overrides unprompted.

## Finishing
Review and verify per the system prompt's arc. Load the \`${SKILL_NAMES.reviewVideo}\` skill
before starting the review pass — it has the procedure and the pass/fail bar.
On top of that standard review, confirm:
- The video actually rotates across several scene design categories, not just
  one or two.
- The script reads as one continuous, informative explanation, not a set of
  disconnected facts.
- The close matches the user's CTA answer — a direct call to action if they
  asked for one, a genuine wrap-up if they didn't.
- Multi-point passages are anchored correctly — each point's text is on screen
  exactly when that point is spoken, not early or late.
- **Spot-check several scenes against their transcript span, in both
  directions**: does the displayed text match what's spoken during that
  overlay's \`words\` range — not a shortened or paraphrased version showing
  less than what's said, and not extra written text (often in a secondary
  asset) showing more than what's said? Text amount should never be an
  independent target; it's whatever was actually spoken in that window.
- Scenes carry a real amount of text to read, not bare fragments — but each
  scene should still be short and hold one idea. If \`${TOOL_NAMES.lintVideo}\`'s
  \`scene_reading_time\` flags a scene as too short for its text, check
  whether that's because too much was stacked into one scene (split it) or
  because unspoken filler text was added to a secondary asset (remove it) —
  don't fix it by just extending the duration.
- **Check the other end of that same problem: is any scene's text flashing
  by too fast to read**, even though it matches its spoken words? That means
  its overlay's \`words\` span was cut too short (a couple of words rather
  than a full clause). Fix it by merging that span with its neighbor at the
  nearest natural clause boundary — not by slowing down the whole video or
  padding the text with words that weren't spoken.
- The video is a run of many short scenes, not a few long ones — if scenes
  are running long, look for ones covering more than one idea and split them.
  Neither direction should feel like a flicker or a drag — read a handful of
  scenes back to back and confirm the pace feels brisk but comfortable.
- Captions are off unless the user asked for them on.`,
};
